import React, { useEffect, useState } from 'react';
import { navLinks, BRAND_WORDMARK } from '../mock';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
      const sections = navLinks.map((l) => l.href.replace('#', ''));
      for (const s of sections) {
        const el = document.getElementById(s);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 140 && rect.bottom >= 140) {
            setActiveSection(s);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-[0_1px_20px_rgba(0,0,0,0.05)] border-b border-slate-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-4 lg:py-5 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => handleClick(e, '#home')}
          className="flex items-center gap-2 shrink-0 group"
        >
          <div className="flex flex-col leading-none">
            <img
              src={BRAND_WORDMARK}
              alt="Tropic Blue"
              className="h-[56px] lg:h-[68px] w-auto object-contain"
            />
            <span className="text-[8.5px] lg:text-[9px] tracking-[0.28em] text-[#0f2c6b]/80 font-medium mt-1">
              PREMIUM PACKAGED DRINKING WATER
            </span>
          </div>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-7 xl:gap-8">
          {navLinks.map((link) => {
            const id = link.href.replace('#', '');
            const isActive = activeSection === id;
            return (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleClick(e, link.href)}
                className={`text-[11.5px] xl:text-[12px] font-medium tracking-[0.15em] transition-colors duration-300 relative ${
                  isActive ? 'text-[#0f2c6b]' : 'text-slate-600 hover:text-[#0f2c6b]'
                }`}
              >
                {link.label}
                <motion.span
                  className="absolute -bottom-1.5 left-0 h-[1px] bg-[#c4a24a]"
                  animate={{ width: isActive ? '100%' : '0%' }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                />
              </a>
            );
          })}
          <a
            href="#contact"
            onClick={(e) => handleClick(e, '#contact')}
            className="premium-button relative bg-[#0f2c6b] hover:bg-[#1e3a8a] text-white text-[11.5px] xl:text-[12px] font-medium tracking-[0.2em] px-6 py-3 rounded-none transition-all duration-300"
          >
            ENQUIRE NOW
          </a>
        </nav>

        {/* Mobile Toggle */}
        <button className="lg:hidden text-[#0f2c6b]" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-slate-100 shadow-lg overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleClick(e, link.href)}
                  className="text-[13px] font-medium tracking-widest text-slate-700 hover:text-[#0f2c6b] py-2"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => handleClick(e, '#contact')}
                className="bg-[#0f2c6b] text-white text-[13px] font-semibold tracking-widest px-6 py-3 text-center mt-2"
              >
                ENQUIRE NOW
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
